import { ajvResolver } from '@hookform/resolvers/ajv';
import { ArrowForward as ArrowForwardIcon } from '@mui/icons-material';
import { Grid } from '@mui/material';
import type { JSONSchemaType } from 'ajv';
import { merge } from 'lodash';
import { useEffect, useRef } from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { SidebarAccordion } from 'src/components/accordion';
import { AllowAdsToggle } from 'src/components/allowAdsToggle/AllowAdsToggle';
import { useEditComponentPageContext } from 'src/components/context/components/EditComponentPageContext';
import { Drawer } from 'src/components/drawer';
import { SidebarHeader } from 'src/components/header';
import { Snackbar } from 'src/components/snackbar';
import {
  SelectDropdownWidget,
  SwitchButtonWidget,
} from 'src/components/widget';
import { ResourceSelectorSidebar } from 'src/components/widget/resourceSelectorWidget/resourceSelectorSidebarWidget';
import { ResourceType } from 'src/components/widget/resourceSelectorWidget/resourceSelectorSidebarWidget/ResourceSelectorSidebar.interface';
import { useGetThemes } from 'src/hooks';
import { type IGcfComponentConfig } from 'src/interfaces';
import { lang } from 'src/utils';
import { handleComponentConfigChange } from 'src/utils/changeHandler';

import { InfoBox } from '../../modules/InfoBox';
import { useThemeOptions } from '../FormSelector.hook';
import { CommonFormProps } from '../FormSelector.interface';
import { FormWrapper } from '../FormWrapper';
import { HeaderTextStylingAccordion } from '../modules/headerTextStylingAccordion/HeaderTextStylingAccordion';
import { HeaderTextStylingOptions } from '../modules/headerTextStylingAccordion/HeaderTextStylingAccordion.interface';
import { ProductsDrawer } from '../modules/productsDrawer';
import {
  SidebarContainer,
  SidebarContent,
} from '../singleShelf/SingleShelfForm.styled';
import { usePromotionalSpaceForm } from './hooks/PromotionalSpaceForm.hook';
import {
  PromotionalSpaceV3Interface as IPromotionalFormConfig,
  PromotionalSpaceV3Schema,
} from './PromotionalSpace.interface';
import { ContentContainer } from './PromotionalSpace.styled';
import { validateAnimationDelays } from './PromotionalSpaceForm.helper';

export const PromotionalSpaceForm = (props: CommonFormProps) => {
  const showPlaceholderRef = useRef(false);
  const {
    control,
    handleSubmit: submitWrapper,
    reset,
    formState,
    getValues,
    setValue,
  } = useForm<IGcfComponentConfig>({
    values: merge(
      {},
      {
        ads: {
          // showPlaceholder is used only for CC previews and is removed before saving the component.
          showPlaceholder: showPlaceholderRef.current,
        },
      },
      props?.componentDetails?.config ?? {},
    ),
    resolver: ajvResolver(
      PromotionalSpaceV3Schema as unknown as JSONSchemaType<IPromotionalFormConfig>,
    ),
  });

  useEffect(() => {
    if (
      props.componentDetails?.config?.ads?.showPlaceholder === undefined &&
      props.componentDetails?.config?.ads?.showPlaceholder !==
        showPlaceholderRef.current
    ) {
      handleOnChange('ads.showPlaceholder', showPlaceholderRef.current);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.componentDetails?.config?.ads?.showPlaceholder]);

  const {
    openBackgroundDrawer,
    openTitleDrawer,
    openComponentSetting,
    openProductsDrawer,
    onToggleBackgroundDrawer,
    onToggleTitleDrawer,
    onToggleComponentSetting,
    onToggleProductsDrawer,
  } = usePromotionalSpaceForm();

  const { data: themes = [] } = useGetThemes();
  const { handleChangeComponentDetails } = useEditComponentPageContext();
  const themeOptions = useThemeOptions();

  const handleOnChange = (key: string, value: unknown) => {
    props.onChange(props.componentDetails.config ?? {}, key, value);
  };

  const headerTextStyleHasErrors = () => {
    if (
      formState.errors.productList?.config?.subProductsDisplayFormat ||
      formState.errors.productList?.config?.sizeLabel ||
      formState.errors.productList?.config?.sizes
    )
      return true;
    else return false;
  };
  const hasBackgroundImage = (mop?: { background?: string }) =>
    typeof mop?.background === 'string' && mop.background.trim() !== '';

  const items = [
    {
      label: lang.allowAds,
      content: <div />,
      chevronIcon: (
        <AllowAdsToggle
          control={control}
          handleOnChange={handleOnChange}
          setValue={setValue}
          showPlaceholderRef={showPlaceholderRef}
        />
      ),
      disabledExpand: true,
      hasError: false,
    },
    {
      label: lang.componentSettings,
      onLabelClick: onToggleComponentSetting,
      content: <div />,
      chevronIcon: <ArrowForwardIcon />,
      disabledExpand: true,
      hasError: false,
    },
    {
      label: lang.background,
      onLabelClick: onToggleBackgroundDrawer,
      content: <div />,
      chevronIcon: <ArrowForwardIcon />,
      disabledExpand: true,
      hasError: props.hasTriedSaving
        ? !hasBackgroundImage(props.componentDetails.config)
        : false,
    },
    {
      label: lang.headerTextStyling,
      onLabelClick: onToggleTitleDrawer,
      content: <div />,
      chevronIcon: <ArrowForwardIcon />,
      disabledExpand: true,
      hasError: headerTextStyleHasErrors(),
    },
    {
      label: lang.products,
      onLabelClick: onToggleProductsDrawer,
      content: <div />,
      chevronIcon: <ArrowForwardIcon />,
      disabledExpand: true,
      hasError: false,
    },
  ];

  const handleSubmit: SubmitHandler<IGcfComponentConfig> = (value) => {
    if (validateAnimationDelays(value.productList)) {
      const { ads, ...rest } = value;

      const payload: IGcfComponentConfig = { ...rest };

      if (ads?.isAdsEnabled !== undefined) {
        payload.ads = { isAdsEnabled: ads.isAdsEnabled };
      }

      props.onSave(payload);
    } else {
      Snackbar.error(lang.failedToSave);
    }
  };

  const handleThemeChange = (themeId: unknown) => {
    const theme = themes.find((theme) => theme.id === themeId);

    if (theme) {
      handleChangeComponentDetails([themeId, theme], ['theme_id', 'theme']);
    }
  };

  return (
    <FormWrapper
      formState={formState}
      formReset={reset}
      formGetValues={getValues}
      onSave={submitWrapper(handleSubmit)}>
      <SidebarAccordion items={items} />
      <Controller
        name="background"
        control={control}
        render={({ field, fieldState }) => (
          <ResourceSelectorSidebar
            label={lang.background}
            type={ResourceType.BACKGROUND}
            defaultAssetName={field.value}
            openResourceSelectorDrawer={openBackgroundDrawer}
            onToggleResourceSelectorDrawer={onToggleBackgroundDrawer}
            onChange={(value) => {
              handleOnChange('background', value);
            }}
            hasError={!!fieldState.error}
            error={fieldState.error?.message || false}
          />
        )}
      />
      <Drawer open={openTitleDrawer}>
        <SidebarContainer>
          <SidebarHeader
            label={lang.headerTextStyling}
            onBackClick={onToggleTitleDrawer}
          />
          <SidebarContent>
            <HeaderTextStylingAccordion
              componentConfig={props.componentDetails.config}
              handleOnChange={handleOnChange}
              errors={formState?.errors}
              enableIndividualStyles={false}
              theme={props.componentDetails.theme}
              hidden={[
                HeaderTextStylingOptions.HEADER,
                HeaderTextStylingOptions.SUBHEADER,
                HeaderTextStylingOptions.DISCLAIMER,
                HeaderTextStylingOptions.MOLECULE_DISPLAY_LABEL,
              ]}
            />
          </SidebarContent>
        </SidebarContainer>
      </Drawer>

      <Drawer open={openComponentSetting}>
        <SidebarHeader
          label={lang.componentSettings}
          onBackClick={onToggleComponentSetting}
        />
        <ContentContainer>
          <Grid container spacing={2} style={{ padding: '26px' }}>
            <Grid item xs={12}>
              <Controller
                name="isFlavourCycling"
                control={control}
                defaultValue={false}
                render={({ field: { ref, ...field } }) => (
                  <SwitchButtonWidget
                    {...field}
                    label="Display flavor cycling"
                    ref={ref}
                    onChange={(value) =>
                      handleComponentConfigChange(
                        props,
                        'isFlavourCycling',
                        value,
                        field.onChange,
                      )
                    }
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <Controller
                name="showComponentProductDisclaimer"
                control={control}
                defaultValue={false}
                render={({ field: { ref, ...field } }) => (
                  <SwitchButtonWidget
                    {...field}
                    label="Show product disclaimers"
                    ref={ref}
                    onChange={(value) =>
                      handleComponentConfigChange(
                        props,
                        'showComponentProductDisclaimer',
                        value,
                        field.onChange,
                      )
                    }
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <SelectDropdownWidget
                label={lang.selectTheme}
                selectOptions={themeOptions}
                value={props.componentDetails?.theme_id}
                onChange={handleThemeChange}
              />
              <InfoBox>{lang.changeThemeInfoText}</InfoBox>
            </Grid>
          </Grid>
        </ContentContainer>
      </Drawer>

      <ProductsDrawer
        open={openProductsDrawer}
        onClose={onToggleProductsDrawer}
        handleOnChange={handleOnChange}
        componentDetails={props.componentDetails}
      />
    </FormWrapper>
  );
};
